/**
 * For more information about the chat events check the official documentation:
 * @see https://developer.salesforce.com/docs/service/messaging-web/guide/event-listeners.html
 */

import React, { createContext, useContext, useEffect, useCallback, ReactNode } from 'react';
import { useEnhancedChatWidget } from '../../hooks/useEnhancedChatWidget';
import safeWindow from '../../utils/safeWindows';
import { ChatWidgetStatus } from './types';

import {
  runSetStatus,
  runSetIsWindowOpen,
  runSetIsLegalAccepted,
  runSetError,
  runSetIsAgentsAvailable,
} from './reducers/actions/runs';

import {
  IChatWidgetContext,
  useInitialChatWidgetState,
} from '../../hooks/useInitialChatWidgetState';

const ChatWidgetContext = createContext<IChatWidgetContext | undefined>(undefined);

export function EnhancedWebChatProvider({ children }: { children: ReactNode }) {
  const { launchChat, sendTextMessage: originalSendTextMessage } = useEnhancedChatWidget();

  const { initialState, dispatch, setStoredLegalAccepted } = useInitialChatWidgetState();

  const handleReady = () => {
    dispatch(runSetStatus(ChatWidgetStatus.READY));
    dispatch(runSetError(null));
  };

  const handleStarted = () => {
    dispatch(runSetStatus(ChatWidgetStatus.STARTED));
    dispatch(runSetIsLegalAccepted(true));
    setStoredLegalAccepted(true);
  };

  const handleContinued = () => {
    dispatch(runSetStatus(ChatWidgetStatus.CONTINUED));
  };

  const handleError = (event: Event) => {
    const customEvent = event as CustomEvent<{ message?: string }>;
    dispatch(runSetStatus(ChatWidgetStatus.ERROR));
    dispatch(runSetError(customEvent.detail?.message || 'Unknown error'));
  };

  const handleMinimized = () => {
    dispatch(runSetIsWindowOpen(false));
  };

  const handleMaximized = () => {
    dispatch(runSetIsWindowOpen(true));
  };

  const handleClosed = () => {
    //? INFO: this disptaches when you click on X button and session is finished
    dispatch(runSetIsLegalAccepted(false));
    setStoredLegalAccepted(false);
    dispatch(runSetIsWindowOpen(false));
  };

  const handleBusinessHoursEnded = () => {
    dispatch(runSetIsAgentsAvailable(false));
  };

  const handleWithinBusinessHours = () => {
    dispatch(runSetIsAgentsAvailable(true));
  };

  const handleConversationMessageReceived = (event: Event) => {
    console.log('handleConversationMessageReceived event', event);
  };

  const handleConversationClosed = () => {
    // TODO: should handle some functionality after call session ended?
    // parece que se lanza cuando se envia el evento del "formulario", no antes, lo que significa que el cliente puede cerrar la ventana sin que llegue el formulario nunca

    closeChatSession();
  };

  const handleConversationEnded = () => {
    closeChatSession();
  };

  const handleMessageDelivered = (event: Event) => {
    console.log('handleMessageDelivered event', event);
  };

  const closeChatSession = () => {
    dispatch(runSetStatus(ChatWidgetStatus.ENDED));
    dispatch(runSetIsLegalAccepted(false));
    dispatch(runSetError(null));
  };

  const handleConversationStatus = (event: Event) => {
    const customEvent = event as CustomEvent<{
      conversationStatus: 'Waiting' | 'Active' | 'Ended';
    }>;
    if (customEvent.detail?.conversationStatus === 'Ended') {
      closeChatSession();
    }
  };

  const handleConversationParticipantChanged = (event: Event) => {
    console.log('handleConversationParticipantChanged event', event);
  };

  const handleConversationRouted = (event: Event) => {
    console.log('handleConversationRouted event', event);
  };

  const handleLaunchChat = useCallback(() => {
    handleMaximized();
    launchChat();
  }, [launchChat, handleMaximized]);

  const handleSendTextMessage = useCallback(
    (message: string) => {
      if (!initialState.isLegalAccepted) {
        console.warn('Cannot send message: Legal not accepted yet.');
        return;
      }
      originalSendTextMessage(message);
    },
    [initialState.isLegalAccepted, originalSendTextMessage, launchChat],
  );

  useEffect(() => {
    safeWindow.addEventListener('onEmbeddedMessagingReady', handleReady);
    safeWindow.addEventListener('onEmbeddedMessagingConversationStarted', handleStarted);
    safeWindow.addEventListener('onEmbeddedMessagingConversationContinued', handleContinued);
    safeWindow.addEventListener('onEmbeddedMessagingError', handleError);
    safeWindow.addEventListener('onEmbeddedMessagingWindowMinimized', handleMinimized);
    safeWindow.addEventListener('onEmbeddedMessagingWindowMaximized', handleMaximized);
    safeWindow.addEventListener('onEmbeddedMessagingWindowClosed', handleClosed);
    safeWindow.addEventListener('onEmbeddedMessagingBusinessHoursEnded', handleBusinessHoursEnded);
    safeWindow.addEventListener('onEmbeddedMessagingConversationClosed', handleConversationClosed);
    safeWindow.addEventListener('onEmbeddedMessagingConversationEnded', handleConversationEnded);
    safeWindow.addEventListener('onEmbeddedMessagingConversationRouted', handleConversationRouted);
    safeWindow.addEventListener('onEmbeddedMessageDelivered', handleMessageDelivered);
    safeWindow.addEventListener(
      'onEmbeddedMessagingConversationParticipantChanged',
      handleConversationParticipantChanged,
    );
    safeWindow.addEventListener('onEmbeddedMessagingSessionStatusUpdate', handleConversationStatus);
    safeWindow.addEventListener(
      'onEmbeddedMessagingWithinBusinessHours',
      handleWithinBusinessHours,
    );
    // safeWindow.addEventListener('onEmbeddedMessagingButtonClicked', handleMaximized);
    safeWindow.addEventListener(
      'onEmbeddedMessagingConversationMessageReceived',
      handleConversationMessageReceived,
    ); // To implement a badge for "mensajes no leidos"

    return () => {
      safeWindow.removeEventListener('onEmbeddedMessagingReady', handleReady);
      safeWindow.removeEventListener('onEmbeddedMessagingConversationStarted', handleStarted);
      safeWindow.removeEventListener('onEmbeddedMessagingConversationContinued', handleContinued);
      safeWindow.removeEventListener('onEmbeddedMessagingError', handleError);
      safeWindow.removeEventListener('onEmbeddedMessagingWindowMinimized', handleMinimized);
      safeWindow.removeEventListener('onEmbeddedMessagingWindowMaximized', handleMaximized);
      safeWindow.removeEventListener('onEmbeddedMessagingWindowClosed', handleClosed);
      safeWindow.removeEventListener(
        'onEmbeddedMessagingBusinessHoursEnded',
        handleBusinessHoursEnded,
      );
      safeWindow.removeEventListener(
        'onEmbeddedMessagingConversationClosed',
        handleConversationClosed,
      );
      safeWindow.removeEventListener(
        'onEmbeddedMessagingConversationEnded',
        handleConversationEnded,
      );
      safeWindow.removeEventListener(
        'onEmbeddedMessagingWithinBusinessHours',
        handleWithinBusinessHours,
      );
      safeWindow.removeEventListener(
        'onEmbeddedMessagingConversationParticipantChanged',
        handleConversationParticipantChanged,
      );
      safeWindow.removeEventListener(
        'onEmbeddedMessagingConversationRouted',
        handleConversationRouted,
      );
      safeWindow.removeEventListener('onEmbeddedMessageDelivered', handleMessageDelivered);
      safeWindow.removeEventListener(
        'onEmbeddedMessagingSessionStatusUpdate',
        handleConversationStatus,
      );
      safeWindow.removeEventListener(
        'onEmbeddedMessagingConversationMessageReceived',
        handleConversationMessageReceived,
      );
    };
  }, []);

  return (
    <ChatWidgetContext.Provider
      value={{
        state: initialState,
        dispatch,
        launchChat: handleLaunchChat,
        sendTextMessage: handleSendTextMessage,
      }}
    >
      {children}
    </ChatWidgetContext.Provider>
  );
}

export const useChatWidgetContext = () => {
  const context = useContext(ChatWidgetContext);
  if (context === undefined) {
    throw new Error('useChatWidgetContext must be used within an EnhancedWebChatProvider');
  }
  return context;
};
